// @ts-nocheck
import React, { useState } from 'react';
import { FollowerGroup, Community } from '../types';
import { stickerTextStyle } from './StickerText';
import { CloseIcon, CheckmarkIcon, UsersIcon, HashtagIcon, PrivateIcon } from './Icons';
import { useAuth } from '../contexts/AuthContext';
import { PostCreator } from './PostCreator';
import { Capacitor } from '@capacitor/core';

interface LinkModalProps {
    isOpen: boolean;
    onClose: () => void;
    friends: string[];
    followers: string[];
    followerGroups: FollowerGroup[];
    communities: Community[];
    onSubmit: (targetUsers: string[], text?: string, imageFile?: File, sendLocation?: boolean, mediaUrl?: string, eventDate?: string, eventTime?: string) => void;
    themeColor?: string;
}

export const LinkModal: React.FC<LinkModalProps> = ({
    isOpen,
    onClose,
    friends,
    followers,
    followerGroups,
    communities,
    onSubmit,
    themeColor
}) => {
    const { currentUser, usersData } = useAuth();
    const currentUserData = usersData[currentUser || ''];
    const userThemeColor = themeColor || currentUserData?.appThemeColor || '#3b82f6';
    const userTextColor = currentUserData?.appTextColor || '#ffffff';
    const userOpacity = (currentUserData?.postBlurOpacity !== undefined ? currentUserData.postBlurOpacity : 50) / 100;

    const [selectedFollowers, setSelectedFollowers] = useState<Set<string>>(new Set());
    const [selectedGroups, setSelectedGroups] = useState<Set<string>>(new Set());
    const [selectedCommunities, setSelectedCommunities] = useState<Set<string>>(new Set());

    const [text, setText] = useState('');
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [mediaUrl, setMediaUrl] = useState<string | null>(null);
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const [sendLocation, setSendLocation] = useState(false);

    // Event Date/Time fields
    const [eventDate, setEventDate] = useState('');
    const [eventTime, setEventTime] = useState('');
    const [showDateTimePicker, setShowDateTimePicker] = useState(false);

    const [isFollowersExpanded, setIsFollowersExpanded] = useState(true);
    const [isGroupsExpanded, setIsGroupsExpanded] = useState(true);
    const [isCommunitiesExpanded, setIsCommunitiesExpanded] = useState(true);

    const getExactModalBg = (color: string, opacityVal: number) => {
        if (!color) return `rgba(26, 26, 46, ${opacityVal})`;
        if (color.startsWith('rgba')) return color.replace(/[\d.]+\)$/, `${opacityVal})`);
        if (color.startsWith('rgb')) return color.replace('rgb', 'rgba').replace(')', `, ${opacityVal})`);
        let hex = color.replace('#', '');
        if (hex.length === 3 || hex.length === 4) hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        return (!isNaN(r) && !isNaN(g) && !isNaN(b)) ? `rgba(${r}, ${g}, ${b}, ${opacityVal})` : `rgba(26, 26, 46, ${opacityVal})`;
    };
    const modalBg = getExactModalBg(userThemeColor, userOpacity);

    const combinedConnections = Array.from(new Set([...(friends || []), ...(followers || [])]));
    const followerLabel = currentUserData?.followerNickname || 'Followers';
    const isSubmitDisabled = selectedFollowers.size === 0 && selectedGroups.size === 0 && selectedCommunities.size === 0;

    const handleSubmit = () => {
        const targetUsers = new Set<string>();

        // Gather all selected individual followers
        selectedFollowers.forEach(id => targetUsers.add(id));

        // Gather users from selected follower groups
        selectedGroups.forEach(id => {
            const group = followerGroups.find(g => g.id === id);
            if (group && group.followers) {
                group.followers.forEach(m => targetUsers.add(m));
            }
        });

        // Gather users from selected communities
        selectedCommunities.forEach(id => {
            const comm = communities.find(c => c._id === id);
            if (comm && comm.members) {
                comm.members.forEach(m => targetUsers.add(m));
            }
        });

        onSubmit(
            Array.from(targetUsers),
            text,
            imageFile || undefined,
            sendLocation,
            mediaUrl || undefined,
            showDateTimePicker && eventDate ? eventDate : undefined,
            showDateTimePicker && eventTime ? eventTime : undefined
        );
        onClose();
    };


    if (!isOpen) return null;

    const toggleSelection = (id: string, set: Set<string>, setter: React.Dispatch<React.SetStateAction<Set<string>>>) => {
        const newSet = new Set(set);
        if (newSet.has(id)) {
            newSet.delete(id);
        } else {
            newSet.add(id);
        }
        setter(newSet);
    };

    const ChevronIcon = ({ expanded }: { expanded: boolean }) => (
        <svg
            className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
            fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
        >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
    );

    return (
        <div 
            className="fixed inset-0  backdrop-blur-lg z-50 flex items-center justify-center animate-fade-in p-0 md:p-8" 
            style={{ backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}
            onClick={onClose}
        >
            <div
                className="relative w-full h-full md:h-[85vh] max-h-full md:max-w-3xl lg:max-w-5xl flex flex-col overflow-hidden shadow-2xl md:rounded-[2.5rem] border border-white/10"
                style={{ backgroundColor: modalBg, color: userTextColor }}
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div 
                    className="flex items-center justify-between p-6 shrink-0 bg-transparent"
                    style={{ paddingTop: Capacitor.isNativePlatform() ? 'calc(1.5rem + env(safe-area-inset-top, 0px))' : '1.5rem' }}
                >
                    <div>
                        <h2 className="text-xl font-bold" style={{ color: userThemeColor, ...stickerTextStyle(0.15) }}>Who you tryna link with?</h2>
                        <p className="text-xs opacity-75 mt-1" style={stickerTextStyle(0.18)}>Ask your friends to hang out! Send them a pin!</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 rounded-full hover:bg-white/10 transition-colors shrink-0"
                    >
                        <CloseIcon className="w-5 h-5" style={{ color: userTextColor }} />
                    </button>
                </div>

                {/* Body - Scrollable */}
                <div
                    className="p-6 overflow-y-auto space-y-2 flex-1 no-scrollbar"
                    onTouchStart={(e) => e.stopPropagation()}
                    onTouchMove={(e) => e.stopPropagation()}
                >
                    {/* PostCreator Integration for Link Request Composition */}
                        <PostCreator
                            onCreatePost={() => {}}
                            onContentChange={(newText, newAttachments) => {
                                setText(newText);
                                if (newAttachments.length > 0) {
                                    const first = newAttachments[0];
                                    if (first.file) {
                                        setImageFile(first.file);
                                        setMediaUrl(null);
                                    } else if (first.url) {
                                        setImageFile(null);
                                        setMediaUrl(first.url);
                                    }
                                } else {
                                    setImageFile(null);
                                    setMediaUrl(null);
                                }
                            }}
                            postBlurOpacity={userOpacity * 100}
                            themeColor={userThemeColor}
                            placeholder="What's the move?"
                            isLinkRequest={true}
                        />
                   

                    {/* Location Toggle */}
                    <div className="flex items-center justify-between p-3 bg-black/20 border border-white/5 rounded-2xl">
                        <div className="flex flex-col text-left">
                            <span className="text-sm font-semibold" style={stickerTextStyle(0.15)}>Send Location</span>
                            <span className="text-[10px] opacity-60" style={stickerTextStyle(0.18)}>Drop them a pin!</span>
                        </div>
                        <button
                            type="button"
                            onClick={() => setSendLocation(!sendLocation)}
                            className={`w-11 h-6 flex items-center rounded-full p-1 cursor-pointer transition-colors duration-300 ${sendLocation ? 'bg-green-500' : 'bg-white/10'}`}
                        >
                            <div
                                className={`bg-white w-4 h-4 rounded-full shadow-md transform transition-transform duration-300 ${sendLocation ? 'translate-x-5' : 'translate-x-0'}`}
                            />
                        </button>
                    </div>

                    {/* Date & Time Selection */}
                    <div className="p-3 bg-black/20 border border-white/5 rounded-2xl space-y-3">
                        <div className="flex items-center justify-between">
                            <div className="flex flex-col text-left">
                                <span className="text-sm font-semibold flex items-center gap-1.5" style={stickerTextStyle(0.15)}>
                                    <span>📅</span> Set Date & Time
                                </span>
                                <span className="text-[10px] opacity-60" style={stickerTextStyle(0.18)}>When are you linking up?</span>
                            </div>
                            <button
                                type="button"
                                onClick={() => setShowDateTimePicker(!showDateTimePicker)}
                                className={`w-11 h-6 flex items-center rounded-full p-1 cursor-pointer transition-colors duration-300 ${showDateTimePicker ? 'bg-green-500' : 'bg-white/10'}`}
                            >
                                <div
                                    className={`bg-white w-4 h-4 rounded-full shadow-md transform transition-transform duration-300 ${showDateTimePicker ? 'translate-x-5' : 'translate-x-0'}`}
                                />
                            </button>
                        </div>

                        {showDateTimePicker && (
                            <div className="grid grid-cols-2 gap-3 pt-2 animate-fade-in border-t border-white/5">
                                <div className="flex flex-col gap-1 text-left">
                                    <label className="text-[10px] uppercase font-bold text-white/70" style={stickerTextStyle(0.12)}>Date</label>
                                    <input
                                        type="date"
                                        value={eventDate}
                                        onChange={(e) => setEventDate(e.target.value)}
                                        className="bg-black/30 border border-white/10 focus:border-white/30 rounded-xl p-2 text-xs text-white outline-none"
                                        style={{ colorScheme: 'dark' }}
                                    />
                                </div>
                                <div className="flex flex-col gap-1 text-left">
                                    <label className="text-[10px] uppercase font-bold text-white/70" style={stickerTextStyle(0.12)}>Time</label>
                                    <input
                                        type="time"
                                        value={eventTime}
                                        onChange={(e) => setEventTime(e.target.value)}
                                        className="bg-black/30 border border-white/10 focus:border-white/30 rounded-xl p-2 text-xs text-white outline-none"
                                        style={{ colorScheme: 'dark' }}
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Follower Groups */}
                    {followerGroups.length > 0 && (
                        <div mt-6>
                            <button
                                onClick={() => setIsGroupsExpanded(!isGroupsExpanded)}
                                className="w-full flex items-center justify-between mb-3 group"
                            >
                                <h3 className="text-sm font-semibold uppercase tracking-wider flex items-center gap-2 opacity-80 group-hover:opacity-100 transition-opacity" style={stickerTextStyle(0.15)}>
                                    <UsersIcon className="w-4 h-4" /> Groups
                                </h3>
                                <ChevronIcon expanded={isGroupsExpanded} />
                            </button>
                            {isGroupsExpanded && (
                                <div className="flex flex-wrap gap-2 animate-fade-in">
                                    {followerGroups.map(group => {
                                        const isSelected = selectedGroups.has(group.id);
                                        return (
                                            <button
                                                key={group.id}
                                                type="button"
                                                onClick={() => toggleSelection(group.id, selectedGroups, setSelectedGroups)}
                                                className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs transition-colors border ${
                                                    isSelected ? 'bg-blue-500/30 border-blue-400' : 'bg-black/30 border-white/10 hover:bg-white/10'
                                                }`}
                                            >
                                                <div className="w-5 h-5 rounded-full bg-blue-600/40 flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0">
                                                    👥
                                                </div>
                                                <span className="truncate max-w-[120px] text-white font-medium">{group.name}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Communities */}
                    {communities.length > 0 && (
                        <div>
                            <button
                                onClick={() => setIsCommunitiesExpanded(!isCommunitiesExpanded)}
                                className="w-full flex items-center justify-between mb-3 group"
                            >
                                <h3 className="text-sm font-semibold uppercase tracking-wider flex items-center gap-2 opacity-80 group-hover:opacity-100 transition-opacity" style={stickerTextStyle(0.15)}>
                                    <PrivateIcon className="w-4 h-4" /> Communities
                                </h3>
                                <ChevronIcon expanded={isCommunitiesExpanded} />
                            </button>
                            {isCommunitiesExpanded && (
                                <div className="flex flex-wrap gap-2 animate-fade-in">
                                    {communities.map(comm => {
                                        const isSelected = selectedCommunities.has(comm._id);
                                        return (
                                            <button
                                                key={comm._id}
                                                type="button"
                                                onClick={() => toggleSelection(comm._id, selectedCommunities, setSelectedCommunities)}
                                                className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs transition-colors border ${
                                                    isSelected ? 'bg-blue-500/30 border-blue-400' : 'bg-black/30 border-white/10 hover:bg-white/10'
                                                }`}
                                            >
                                                {comm.avatar ? (
                                                    <img src={comm.avatar} alt="" className="w-5 h-5 rounded-full object-cover flex-shrink-0" />
                                                ) : (
                                                    <div className="w-5 h-5 rounded-full bg-indigo-600/40 flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0">
                                                        #
                                                    </div>
                                                )}
                                                <span className="truncate max-w-[120px] text-white font-medium">{comm.name}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Individual Followers & Friends */}
                    {combinedConnections.length > 0 && (
                        <div>
                            <button
                                onClick={() => setIsFollowersExpanded(!isFollowersExpanded)}
                                className="w-full flex items-center justify-between mb-3 group"
                            >
                                <h3 className="text-sm font-semibold uppercase tracking-wider opacity-80 group-hover:opacity-100 transition-opacity" style={stickerTextStyle(0.15)}>
                                    {followerLabel}
                                </h3>
                                <ChevronIcon expanded={isFollowersExpanded} />
                            </button>
                            {isFollowersExpanded && (
                                <div className="flex flex-wrap gap-2 animate-fade-in">
                                    {combinedConnections.map(username => {
                                        const isSelected = selectedFollowers.has(username);
                                        const u = usersData[username];
                                        return (
                                            <button
                                                key={username}
                                                type="button"
                                                onClick={() => toggleSelection(username, selectedFollowers, setSelectedFollowers)}
                                                className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs transition-colors border ${
                                                    isSelected ? 'bg-blue-500/30 border-blue-400' : 'bg-black/30 border-white/10 hover:bg-white/10'
                                                }`}
                                            >
                                                {u?.pfp ? (
                                                    <img src={u.pfp} alt="" className="w-5 h-5 rounded-full object-cover flex-shrink-0" />
                                                ) : (
                                                    <div className="w-5 h-5 rounded-full bg-gray-500 flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0">
                                                        {username[0]?.toUpperCase()}
                                                    </div>
                                                )}
                                                <span className="truncate max-w-[120px] text-white font-medium">{u?.displayName || username}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}

                    {combinedConnections.length === 0 && followerGroups.length === 0 && communities.length === 0 && (
                        <div className="text-center py-8 opacity-50 text-sm" style={stickerTextStyle(0.18)}>
                            You don't have anyone to send a link request to yet.
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-6 shrink-0 bg-transparent">
                    <button
                        onClick={handleSubmit}
                        disabled={isSubmitDisabled}
                        style={{
                            backgroundColor: isSubmitDisabled ? 'rgba(255, 255, 255, 0.05)' : userThemeColor,
                            color: isSubmitDisabled ? 'rgba(255, 255, 255, 0.2)' : '#ffffff',
                            ...(!isSubmitDisabled ? stickerTextStyle(0.15) : {})
                        }}
                        className="w-full py-3.5 rounded-[1.5rem] font-bold text-lg transition-all duration-300"
                    >
                        Send Link Request
                    </button>
                </div>
            </div>
        </div>
    );
};
